/**
 * CompanyDashboard · Web (B2B Phase 3)
 *
 * Dashboard dedicado para `role="company_admin"` e `role="company_staff"`.
 * Espelha o `MunicipioDashboard` mas com scope `company_id` em vez de
 * `municipality_id`.
 *
 * Mostra:
 *   - Header com nome da empresa + logo + logout
 *   - KPIs: rotas da empresa, membros, SOS activos, avisos activos
 *   - SosPanel + AlertsPanel (usam `/api/companies/{id}/sos-live` e
 *     `/api/companies/{id}/hazards/live`)
 *   - Lista das rotas privadas da empresa (usa `/api/routes` já filtrado)
 *   - Lista de membros da empresa
 *
 * Design: paleta Rugged (idêntica ao MunicipioDashboard).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Building2, Users, Route as RouteIcon,
  AlertTriangle, Loader2, RefreshCw, Eye, Send, ShieldAlert, MessageSquare, Trash2, UserPlus, Copy, X, Image as ImageIcon, Bell, Navigation, Pencil, Check, ThumbsUp, MessageCircle, ArrowLeft, List, TrendingUp, Trophy, Key, MapPin, Calendar, Plus, XCircle,
} from 'lucide-react';
import RouteViewerModal from '@/components/RouteViewerModal';
import AlertDetailDialog from '@/components/AlertDetailDialog';
import FleetMapModal from '@/components/FleetMapModal';
import UploadRouteDialog from '@/components/UploadRouteDialog';
import EditRouteDialog from '@/components/EditRouteDialog';
import MeetingPointPicker from '@/components/MeetingPointPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Paleta Rugged (mesma do MunicipioDashboard)
const C = {
  bg: '#2B2A25',
  bg2: '#241E19',
  bg3: '#0F0D0B',
  brand: '#E87A2A',
  brandDim: 'rgba(232,122,42,0.16)',
  sand: '#C4A275',
  sandLine: 'rgba(196,162,117,0.18)',
  ink: '#F2EBDD',
  inkDim: '#8A7A63',
  cardBg: 'rgba(26,22,19,0.82)',
  green: '#3FA34D',
  greenDim: 'rgba(63,163,77,0.16)',
  red: '#DC2626',
};

const HAZARD_ICONS = {
  fire: '🔥', fire_risk: '⛔', wind: '🌬️', rain: '🌧️',
  storm: '⛈️', fog: '🌫️', snow: '❄️', heat: '🌡️',
  cold: '🥶', coastal: '🌊', uv: '☀️', other: '⚠️',
};
const SEV_COLOR = { yellow: '#F5C542', orange: '#F08A2B', red: '#E13B3B' };

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

const KpiCard = ({ icon: Icon, label, value, sub, tint = C.brand }) => (
  <div
    className="rounded-xl p-4 border"
    style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-2 mb-2">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: 'rgba(232,122,42,0.18)' }}
      >
        <Icon className="w-5 h-5" style={{ color: tint }} />
      </div>
      <div className="text-xs uppercase tracking-wider font-bold" style={{ color: C.inkDim }}>
        {label}
      </div>
    </div>
    <div className="text-3xl font-bold" style={{ color: C.ink }}>{value}</div>
    {sub && <div className="text-xs mt-1" style={{ color: C.inkDim }}>{sub}</div>}
  </div>
);

/**
 * SosPanel — SOS activos + resolvidos hoje. Payload vem de
 * `/api/companies/{id}/sos-live` (formato: {sos_active, sos_resolved_today, totals}).
 */
const SosPanel = ({ sosData, onOpen }) => {
  const [expanded, setExpanded] = useState(false);
  const active = sosData?.sos_active || [];
  const resolved = sosData?.sos_resolved_today || [];
  const hasActive = active.length > 0;

  useEffect(() => { if (hasActive) setExpanded(true); }, [hasActive]);

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        backgroundColor: C.cardBg,
        borderColor: hasActive ? C.red : C.sandLine,
        borderWidth: hasActive ? 2 : 1,
        boxShadow: hasActive ? '0 0 24px rgba(220,38,38,0.35)' : 'none',
      }}
      data-testid="company-sos-panel"
    >
      <button
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center gap-3 px-4 py-3"
        style={{ background: 'transparent' }}
        data-testid="company-sos-toggle-btn"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{
            backgroundColor: hasActive ? 'rgba(220,38,38,0.22)' : 'rgba(0,0,0,0.35)',
            border: `1px solid ${hasActive ? C.red : C.sandLine}`,
            animation: hasActive ? 'pulseSos 1.4s ease-in-out infinite' : 'none',
          }}
        >
          <span style={{ fontSize: 18 }}>🚨</span>
        </div>
        <div className="flex-1 text-left">
          <div className="text-sm font-black tracking-wider uppercase" style={{ color: hasActive ? '#F87171' : C.ink }}>
            {hasActive ? 'SOS EM CURSO' : 'SOS'}
          </div>
          <div className="text-xs mt-0.5" style={{ color: C.inkDim }}>
            {active.length} ativo{active.length === 1 ? '' : 's'} · {resolved.length} resolvido{resolved.length === 1 ? '' : 's'} hoje
          </div>
        </div>
        {active.length > 0 && (
          <span
            className="px-2 py-1 rounded-full text-xs font-black min-w-[24px] text-center"
            style={{ backgroundColor: C.red, color: '#fff' }}
            data-testid="company-sos-active-badge"
          >
            {active.length}
          </span>
        )}
        <span className="text-xs" style={{ color: C.inkDim }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-2" style={{ borderTop: `1px solid ${C.sandLine}` }}>
          {active.length > 0 && (
            <>
              <div className="text-[10px] font-black tracking-widest mt-2 mb-1" style={{ color: '#F87171' }}>
                EM CURSO ({active.length})
              </div>
              {active.slice(0, 10).map((s) => (
                <button
                  key={s.sos_id}
                  type="button"
                  onClick={() => onOpen?.(s)}
                  className="w-full text-left p-3 rounded flex items-start gap-2 hover:brightness-110 transition"
                  style={{ backgroundColor: 'rgba(220,38,38,0.12)', borderLeft: '4px solid #DC2626' }}
                  data-testid={`company-sos-item-${s.sos_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black" style={{ color: C.ink }}>
                      🚨 {s.user_name || 'Membro'} {s.route_name ? `— ${s.route_name}` : ''}
                    </div>
                    <div className="text-[11px] mt-1" style={{ color: C.inkDim }}>
                      {s.message ? `"${s.message}" · ` : ''}
                      {s.lat && s.lon ? `${s.lat.toFixed(4)}, ${s.lon.toFixed(4)} · ` : ''}
                      há {timeAgo(s.created_at)}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}

          {resolved.length > 0 && (
            <>
              <div className="text-[10px] font-black tracking-widest mt-3 mb-1" style={{ color: C.inkDim }}>
                RESOLVIDOS HOJE ({resolved.length})
              </div>
              {resolved.slice(0, 5).map((s) => (
                <button
                  key={s.sos_id}
                  type="button"
                  onClick={() => onOpen?.(s)}
                  className="w-full text-left p-2.5 rounded flex items-start gap-2 opacity-70 hover:opacity-100 transition"
                  style={{ backgroundColor: 'rgba(0,0,0,0.25)', borderLeft: '3px solid #6B7280' }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold line-through" style={{ color: C.inkDim }}>
                      🚨 {s.user_name || 'Membro'} {s.route_name ? `— ${s.route_name}` : ''}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: C.inkDim }}>
                      Resolvido há {timeAgo(s.resolved_at)}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}

          {active.length === 0 && resolved.length === 0 && (
            <div className="text-center py-4 text-xs" style={{ color: C.inkDim }}>
              ✅ Sem SOS registados hoje.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * AlertsPanel — hazards oficiais (IPMA/EFFIS globais) + user_reports scoped.
 * Payload vem de `/api/companies/{id}/hazards/live`.
 */
const AlertsPanel = ({ hazardsData, onOpenHazard, onOpenReport }) => {
  const [expanded, setExpanded] = useState(false);
  const t = hazardsData?.totals || {};
  const total = (t.hazards || 0) + (t.user_reports || 0);
  const hazards = hazardsData?.hazards_oficiais || [];
  const userReports = hazardsData?.user_reports || [];

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: C.cardBg, borderColor: total > 0 ? C.brand : C.sandLine }}
      data-testid="company-alerts-panel"
    >
      <button
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center gap-3 px-4 py-3"
        style={{ background: 'transparent' }}
        data-testid="company-alerts-toggle-btn"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: total > 0 ? C.brandDim : 'rgba(0,0,0,0.35)', border: `1px solid ${total > 0 ? C.brand : C.sandLine}` }}
        >
          <span style={{ fontSize: 18 }}>{total > 0 ? '⚠️' : '🛡️'}</span>
        </div>
        <div className="flex-1 text-left">
          <div className="text-sm font-black tracking-wider uppercase" style={{ color: C.ink }}>
            Avisos
          </div>
          <div className="text-xs mt-0.5" style={{ color: C.inkDim }}>
            {(t.hazards || 0)} IPMA/EFFIS · {(t.user_reports || 0)} reports dos membros
          </div>
        </div>
        {total > 0 && (
          <span
            className="px-2 py-1 rounded-full text-xs font-black min-w-[24px] text-center"
            style={{ backgroundColor: C.brand, color: '#fff' }}
            data-testid="company-alerts-total-badge"
          >
            {total}
          </span>
        )}
        <span className="text-xs" style={{ color: C.inkDim }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3" style={{ borderTop: `1px solid ${C.sandLine}` }}>
          {hazards.length > 0 && (
            <div>
              <div className="text-[10px] font-black tracking-widest mb-1.5" style={{ color: C.brand }}>
                ⚠️ AVISOS OFICIAIS ({hazards.length}) · IPMA · EFFIS
              </div>
              <div className="space-y-1.5">
                {hazards.slice(0, 8).map((h, i) => {
                  const color = SEV_COLOR[h.severity] || SEV_COLOR.yellow;
                  return (
                    <button
                      key={`hz-${i}`}
                      type="button"
                      onClick={() => onOpenHazard?.(h)}
                      className="w-full text-left p-2.5 rounded flex items-start gap-2 hover:brightness-110 transition"
                      style={{ backgroundColor: 'rgba(0,0,0,0.25)', borderLeft: `3px solid ${color}` }}
                      data-testid={`company-hazard-item-${i}`}
                    >
                      <span style={{ fontSize: 18 }}>{HAZARD_ICONS[h.hazard_type] || '⚠️'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold" style={{ color: C.ink }}>
                          {h.title || h.hazard_type}
                          {h.severity && (
                            <span
                              className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-black uppercase"
                              style={{ backgroundColor: color, color: '#fff' }}
                            >
                              {h.severity}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] mt-0.5" style={{ color: C.inkDim }}>
                          {h.meta?.concelho ? `${h.meta.concelho} · ` : ''}
                          {h.description || h.text || 'Sem descrição'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {userReports.length > 0 && (
            <div>
              <div className="text-[10px] font-black tracking-widest mb-1.5" style={{ color: C.sand }}>
                👥 REPORTS DOS MEMBROS ({userReports.length})
              </div>
              <div className="space-y-1.5">
                {userReports.slice(0, 8).map((r) => (
                  <button
                    key={r.report_id}
                    type="button"
                    onClick={() => onOpenReport?.(r)}
                    className="w-full text-left p-2.5 rounded flex items-start gap-2 hover:brightness-110 transition"
                    style={{ backgroundColor: 'rgba(0,0,0,0.25)', borderLeft: '3px solid #F59E0B' }}
                    data-testid={`company-report-item-${r.report_id}`}
                  >
                    <span style={{ fontSize: 16 }}>📍</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold" style={{ color: C.ink }}>
                        {r.type || 'obstáculo'}
                        {r.user_name && (
                          <span className="ml-2 font-normal text-[11px]" style={{ color: C.inkDim }}>
                            · {r.user_name}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: C.inkDim }}>
                        {r.note ? `"${r.note}" · ` : ''}
                        há {timeAgo(r.created_at)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {total === 0 && (
            <div className="text-center py-4 text-xs" style={{ color: C.inkDim }}>
              ✅ Nenhum aviso ativo. Boas condições nas rotas da empresa.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * FleetWidget — barra compacta sticky "who's driving where".
 * Consome `/api/companies/{id}/members-live` a cada 30s.
 * Mostra até 8 membros em scroll horizontal com:
 *   avatar + iniciais + velocidade + tempo desde último update.
 * Fica sempre visível abaixo do header.
 */
const FleetWidget = ({ companyId, onOpenMap }) => {
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const res = await axios.get(`${API}/companies/${companyId}/members-live`, { withCredentials: true });
        if (!cancelled) setLocations(res.data?.locations || []);
      } catch { /* silent */ }
    };
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [companyId]);

  if (locations.length === 0) return null;
  return (
    <div
      className="sticky z-20 backdrop-blur"
      style={{ top: 66, backgroundColor: 'rgba(26,22,19,0.92)', borderBottom: `1px solid ${C.sandLine}` }}
      data-testid="fleet-widget"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 overflow-x-auto no-scrollbar">
        <button
          onClick={onOpenMap}
          className="flex items-center gap-1.5 flex-shrink-0 hover:opacity-100 opacity-90 transition"
          style={{ background: 'transparent' }}
          title="Ver mapa completo da frota"
          data-testid="fleet-widget-open-map"
        >
          <Navigation className="w-3.5 h-3.5" style={{ color: C.brand }} />
          <span className="text-[10px] font-black tracking-widest" style={{ color: C.brand }}>
            FLEET · {locations.length}
          </span>
          <span className="text-[10px]" style={{ color: C.brand }}>›</span>
        </button>
        <div className="flex items-center gap-2 flex-1">
          {locations.slice(0, 12).map((m) => {
            const initials = (m.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
            const ageMin = Math.max(0, Math.round((Date.now() - new Date(m.updated_at).getTime()) / 60000));
            const isFresh = ageMin < 5;
            const kmh = m.speed != null ? Math.round(m.speed * 3.6) : null;
            return (
              <button
                key={m.user_id}
                type="button"
                onClick={onOpenMap}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full flex-shrink-0 hover:brightness-125 transition"
                style={{
                  backgroundColor: isFresh ? 'rgba(63,163,77,0.14)' : 'rgba(0,0,0,0.35)',
                  border: `1px solid ${isFresh ? C.green : C.sandLine}`,
                }}
                title={`${m.name} · ${kmh != null ? `${kmh} km/h · ` : ''}${ageMin === 0 ? 'agora' : `há ${ageMin}min`} · clica para abrir o mapa`}
                data-testid={`fleet-widget-item-${m.user_id}`}
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black"
                  style={{ backgroundColor: C.brand, color: '#fff' }}
                >
                  {initials}
                </div>
                <span className="text-[11px] font-semibold" style={{ color: C.ink }}>
                  {(m.name || '').split(' ')[0]}
                </span>
                {kmh != null && (
                  <span className="text-[10px] font-mono" style={{ color: isFresh ? C.green : C.inkDim }}>
                    {kmh}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * MuralPanel — Feed social interno da empresa (v2 com fotos + important).
 */
const MuralPanel = ({ companyId, me }) => {
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState('');
  const [important, setImportant] = useState(false);
  const [pending, setPending] = useState([]);   // {file, path, preview}
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);

  const canMarkImportant = me && ['admin', 'company_admin', 'company_staff'].includes(me.role);

  const fetchFeed = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/companies/${companyId}/feed`, { withCredentials: true });
      setPosts(res.data?.posts || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const onPickImages = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - pending.length);
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const res = await axios.post(
          `${API}/companies/${companyId}/upload-image`,
          fd, { withCredentials: true },
        );
        setPending((p) => [...p, { file: f, path: res.data.path, preview: URL.createObjectURL(f) }]);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Falha no upload');
      }
    }
    e.target.value = '';  // reset
  };

  const removePending = (idx) => setPending((p) => p.filter((_, i) => i !== idx));

  const submit = async () => {
    const t = text.trim();
    if (!t && pending.length === 0) return;
    setPosting(true);
    try {
      await axios.post(
        `${API}/companies/${companyId}/posts`,
        {
          text: t, important, image_paths: pending.map((p) => p.path),
        },
        { withCredentials: true },
      );
      setText(''); setImportant(false); setPending([]);
      toast.success(important ? 'Publicado — equipa notificada 🔔' : 'Post publicado');
      fetchFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao publicar');
    } finally {
      setPosting(false);
    }
  };

  const doDelete = async (postId) => {
    try {
      await axios.delete(`${API}/companies/${companyId}/posts/${postId}`, { withCredentials: true });
      toast.success('Post apagado');
      fetchFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao apagar');
    }
  };

  const canDelete = (p) => {
    if (!me) return false;
    if (p.user_id === me.user_id) return true;
    return ['admin', 'company_admin', 'company_staff'].includes(me.role);
  };

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
      data-testid="company-mural-panel"
    >
      <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: C.sandLine }}>
        <MessageSquare className="w-4 h-4" style={{ color: C.brand }} />
        <div className="text-sm font-bold uppercase tracking-wider" style={{ color: C.ink }}>
          Mural interno · {posts.length}
        </div>
      </div>
      {/* Publisher */}
      <div className="p-4" style={{ borderBottom: `1px solid ${C.sandLine}` }}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Partilha uma dica, foto de rota ou aviso com a equipa…"
          rows={2}
          maxLength={2000}
          className="w-full px-3 py-2 rounded-md text-sm resize-none"
          style={{ background: 'rgba(0,0,0,0.35)', color: C.ink, border: `1px solid ${C.sandLine}`, outline: 'none' }}
          data-testid="company-mural-input"
        />
        {/* Photo previews */}
        {pending.length > 0 && (
          <div className="flex gap-2 mt-2 overflow-x-auto">
            {pending.map((p, i) => (
              <div key={i} className="relative flex-shrink-0" style={{ width: 80, height: 80 }}>
                <img
                  src={p.preview}
                  alt=""
                  className="w-full h-full object-cover rounded"
                  style={{ border: `1px solid ${C.sandLine}` }}
                />
                <button
                  onClick={() => removePending(i)}
                  className="absolute -top-1.5 -right-1.5 rounded-full w-5 h-5 flex items-center justify-center"
                  style={{ backgroundColor: C.red, color: '#fff' }}
                  title="Remover"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <label
            className="px-2.5 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 cursor-pointer transition"
            style={{
              backgroundColor: 'rgba(232,122,42,0.14)', color: C.brand,
              border: `1px dashed ${C.brand}`, opacity: pending.length >= 4 ? 0.5 : 1,
            }}
            data-testid="company-mural-photo-btn"
          >
            <ImageIcon className="w-3.5 h-3.5" /> Fotos ({pending.length}/4)
            <input
              type="file" accept="image/jpeg,image/png,image/webp"
              multiple hidden onChange={onPickImages}
              disabled={pending.length >= 4}
            />
          </label>
          {canMarkImportant && (
            <label
              className="px-2.5 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 cursor-pointer transition"
              style={{
                backgroundColor: important ? C.red : 'rgba(0,0,0,0.35)',
                color: important ? '#fff' : C.inkDim,
                border: `1px solid ${important ? C.red : C.sandLine}`,
              }}
              data-testid="company-mural-important-toggle"
            >
              <Bell className="w-3.5 h-3.5" />
              {important ? 'Notificar equipa' : 'Marcar importante'}
              <input
                type="checkbox" checked={important}
                onChange={(e) => setImportant(e.target.checked)} hidden
              />
            </label>
          )}
          <span className="text-[10px] flex-1 text-right" style={{ color: C.inkDim }}>
            {text.length}/2000
          </span>
          <button
            onClick={submit}
            disabled={posting || (!text.trim() && pending.length === 0)}
            className="px-4 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-60"
            style={{ backgroundColor: C.brand, color: '#fff' }}
            data-testid="company-mural-submit"
          >
            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Publicar
          </button>
        </div>
      </div>
      {/* Feed */}
      <div className="p-4 space-y-3">
        {loading && posts.length === 0 && (
          <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: C.brand }} /></div>
        )}
        {!loading && posts.length === 0 && (
          <div className="text-center py-6 text-xs" style={{ color: C.inkDim }}>
            📢 Ainda ninguém publicou. Sê o primeiro a partilhar!
          </div>
        )}
        {posts.map((p) => (
          <div
            key={p.post_id}
            className="p-3 rounded flex items-start gap-3"
            style={{
              backgroundColor: p.important ? 'rgba(220,38,38,0.10)' : 'rgba(0,0,0,0.25)',
              borderLeft: `3px solid ${p.important ? C.red : C.brand}`,
            }}
            data-testid={`company-mural-post-${p.post_id}`}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: C.brandDim, color: C.brand }}
            >
              {(p.user_name || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold" style={{ color: C.ink }}>{p.user_name}</span>
                <span className="text-[10px]" style={{ color: C.inkDim }}>
                  {new Date(p.created_at).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
                {p.important && (
                  <span
                    className="text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest flex items-center gap-1"
                    style={{ backgroundColor: C.red, color: '#fff' }}
                  >
                    <Bell className="w-2.5 h-2.5" /> Importante
                  </span>
                )}
              </div>
              {p.text && (
                <div className="text-sm mt-1 whitespace-pre-wrap" style={{ color: C.ink }}>
                  {p.text}
                </div>
              )}
              {Array.isArray(p.image_paths) && p.image_paths.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 mt-2">
                  {p.image_paths.map((path, i) => (
                    <a
                      key={i}
                      href={`${BACKEND_URL}/${path}`}
                      target="_blank" rel="noreferrer"
                      className="block relative"
                      style={{ paddingBottom: '75%' }}
                    >
                      <img
                        src={`${BACKEND_URL}/${path}`}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover rounded cursor-pointer"
                        style={{ border: `1px solid ${C.sandLine}` }}
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              )}
              <PostActions post={p} companyId={companyId} me={me} onChanged={fetchFeed} />
            </div>
            {canDelete(p) && (
              <button
                onClick={() => doDelete(p.post_id)}
                className="p-1 rounded opacity-60 hover:opacity-100 transition"
                title="Apagar"
                data-testid={`company-mural-delete-${p.post_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" style={{ color: C.red }} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * InviteMemberModal — convite por email.
 * POST /api/companies/{id}/invite → devolve link magic (`join_url` + token)
 * que pode ser copiado + partilhado por qualquer canal (email/WhatsApp).
 */
const InviteMemberModal = ({ open, onClose, companyId, canInviteAdmin }) => {
  const [email, setEmail] = useState('');
  const [asRole, setAsRole] = useState('user');
  const [loading, setLoading] = useState(false);
  const [invite, setInvite] = useState(null);
  // Fev/2026 (Ricardo) — mesmo combobox do GroupsManager: procura users já
  // inscritos para evitar que o admin tenha de saber o email exacto.
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const reset = () => { setEmail(''); setAsRole('user'); setInvite(null); setUserResults([]); };
  const handleClose = () => { reset(); onClose?.(); };

  const searchUsers = async (q) => {
    const query = q.trim();
    if (query.length < 2) { setUserResults([]); return; }
    setSearching(true);
    try {
      const { data } = await axios.get(
        `${API}/social/users/search?q=${encodeURIComponent(query)}&limit=10`,
        { withCredentials: true },
      );
      setUserResults(data?.users || []);
    } catch { setUserResults([]); } finally { setSearching(false); }
  };

  const submit = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/companies/${companyId}/invite`,
        { email: email.trim(), as_role: asRole },
        { withCredentials: true },
      );
      setInvite(res.data);
      setUserResults([]);
      toast.success('Convite criado — copia o link e envia');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar convite');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (!invite?.join_url) return;
    const fullUrl = `${window.location.origin}${invite.join_url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copiado');
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
      onClick={handleClose}
    >
      <div
        className="w-full max-w-md rounded-xl border"
        style={{ backgroundColor: C.bg2, borderColor: C.brand }}
        onClick={(e) => e.stopPropagation()}
        data-testid="invite-modal"
      >
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.sandLine}` }}>
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" style={{ color: C.brand }} />
            <h3 className="text-lg font-bold" style={{ color: C.ink }}>Convidar membro</h3>
          </div>
          <button onClick={handleClose} data-testid="invite-modal-close">
            <X className="w-5 h-5" style={{ color: C.inkDim }} />
          </button>
        </div>

        {!invite ? (
          <div className="p-5 space-y-4">
            <div className="relative">
              <label className="text-xs font-bold uppercase tracking-wider" style={{ color: C.inkDim }}>
                Procurar por nome ou email
              </label>
              <input
                type="text"
                value={email}
                onChange={(e) => { setEmail(e.target.value); searchUsers(e.target.value); }}
                placeholder="João, maria@... ou email completo"
                className="w-full mt-1 px-3 py-2 rounded-md text-sm"
                style={{ background: 'rgba(0,0,0,0.35)', color: C.ink, border: `1px solid ${C.sandLine}`, outline: 'none' }}
                data-testid="invite-email-input"
                autoFocus
                autoComplete="off"
              />
              {/* Fev/2026 (Ricardo) — resultados de pesquisa de users já inscritos */}
              {email.trim().length >= 2 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border max-h-56 overflow-y-auto shadow-xl"
                     style={{ backgroundColor: C.bg2, borderColor: C.sandLine }}
                     data-testid="invite-search-results">
                  {searching && (
                    <div className="px-3 py-2 text-xs" style={{ color: C.inkDim }}>A procurar…</div>
                  )}
                  {!searching && userResults.length > 0 && userResults.map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => { setEmail(u.email); setUserResults([]); }}
                      className="w-full text-left px-3 py-2 flex items-center gap-2 border-b last:border-b-0 hover:bg-black/20"
                      style={{ borderColor: C.sandLine }}
                      data-testid={`invite-search-pick-${u.user_id}`}
                    >
                      <div className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold"
                           style={{ backgroundColor: C.brand, color: '#fff' }}>
                        {(u.name || u.email || '?').substring(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>{u.name}</div>
                        <div className="text-xs truncate" style={{ color: C.inkDim }}>{u.email}</div>
                      </div>
                      <span className="text-[10px] font-bold uppercase" style={{ color: C.brand }}>Usar email</span>
                    </button>
                  ))}
                  {!searching && userResults.length === 0 && (
                    <div className="px-3 py-3 text-xs" style={{ color: C.inkDim }}>
                      Sem resultados — escreve o email completo para enviar convite.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider" style={{ color: C.inkDim }}>
                Função
              </label>
              <select
                value={asRole}
                onChange={(e) => setAsRole(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md text-sm"
                style={{ background: 'rgba(0,0,0,0.35)', color: C.ink, border: `1px solid ${C.sandLine}`, outline: 'none' }}
                data-testid="invite-role-select"
              >
                <option value="user">Membro (condutor)</option>
                <option value="company_staff">Staff (modera dentro da empresa)</option>
                {canInviteAdmin && <option value="company_admin">Marshall (gere a empresa)</option>}
              </select>
            </div>
            <button
              onClick={submit}
              disabled={loading || !email.trim()}
              className="w-full py-2.5 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition disabled:opacity-60"
              style={{ backgroundColor: C.brand, color: '#fff' }}
              data-testid="invite-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Gerar convite
            </button>
            <p className="text-xs" style={{ color: C.inkDim }}>
              💡 O sistema gera um link único que expira em 14 dias. Envia-o por email ou WhatsApp — quando o convidado abrir e fizer login, entra automaticamente na empresa.
            </p>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: C.green }}>
              ✓ Convite criado
            </div>
            <div className="text-sm" style={{ color: C.ink }}>Copia e envia este link:</div>
            <div
              className="p-3 rounded font-mono text-xs break-all cursor-pointer"
              style={{ background: 'rgba(0,0,0,0.5)', color: C.brand, border: `1px solid ${C.brand}` }}
              onClick={copyLink}
              data-testid="invite-link-display"
            >
              {window.location.origin}{invite.join_url}
            </div>
            <button
              onClick={copyLink}
              className="w-full py-2 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition"
              style={{ backgroundColor: C.sand, color: C.bg3 }}
              data-testid="invite-copy-btn"
            >
              <Copy className="w-4 h-4" /> Copiar link
            </button>
            <div className="text-xs" style={{ color: C.inkDim }}>
              Expira: {new Date(invite.expires_at).toLocaleDateString('pt-PT')}
            </div>
            <button
              onClick={reset}
              className="w-full py-2 rounded-md text-xs font-bold border transition"
              style={{ borderColor: C.sandLine, color: C.ink }}
              data-testid="invite-new-btn"
            >
              Criar outro convite
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * PostActions — 👍 + 💬 + secção de comentários por post.
 */
const PostActions = ({ post, companyId, me, onChanged }) => {
  const [likes, setLikes] = useState(post.likes || []);
  const [comments, setComments] = useState(post.comments || []);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [sending, setSending] = useState(false);

  const liked = me && likes.includes(me.user_id);

  const toggleLike = async () => {
    try {
      const res = await axios.post(
        `${API}/companies/${companyId}/posts/${post.post_id}/react`,
        {}, { withCredentials: true },
      );
      // Toggle local
      if (res.data.reacted) setLikes((l) => [...l.filter((u) => u !== me.user_id), me.user_id]);
      else setLikes((l) => l.filter((u) => u !== me.user_id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  const addComment = async () => {
    const t = commentText.trim();
    if (!t) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/companies/${companyId}/posts/${post.post_id}/comment`,
        { text: t }, { withCredentials: true },
      );
      setComments((c) => [...c, res.data.comment]);
      setCommentText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    } finally {
      setSending(false);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(
        `${API}/companies/${companyId}/posts/${post.post_id}/comment/${commentId}`,
        { withCredentials: true },
      );
      setComments((c) => c.filter((x) => x.comment_id !== commentId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  const canDeleteComment = (c) => {
    if (!me) return false;
    if (c.user_id === me.user_id) return true;
    return ['admin', 'company_admin', 'company_staff'].includes(me.role);
  };

  return (
    <div className="mt-2">
      <div className="flex items-center gap-3 pt-2" style={{ borderTop: `1px solid ${C.sandLine}` }}>
        <button
          onClick={toggleLike}
          className="flex items-center gap-1 text-xs font-bold transition"
          style={{ color: liked ? C.brand : C.inkDim }}
          data-testid={`post-like-${post.post_id}`}
        >
          <ThumbsUp className="w-3.5 h-3.5" style={{ fill: liked ? C.brand : 'none' }} />
          {likes.length > 0 ? likes.length : 'Gosto'}
        </button>
        <button
          onClick={() => setShowComments((s) => !s)}
          className="flex items-center gap-1 text-xs font-bold transition"
          style={{ color: showComments ? C.brand : C.inkDim }}
          data-testid={`post-comments-toggle-${post.post_id}`}
        >
          <MessageCircle className="w-3.5 h-3.5" />
          {comments.length > 0 ? `${comments.length} comentário${comments.length === 1 ? '' : 's'}` : 'Comentar'}
        </button>
      </div>

      {showComments && (
        <div className="mt-2 space-y-2" data-testid={`post-comments-${post.post_id}`}>
          {comments.map((c) => (
            <div
              key={c.comment_id}
              className="flex items-start gap-2 p-2 rounded"
              style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0"
                style={{ backgroundColor: C.brandDim, color: C.brand }}
              >
                {(c.user_name || '?').slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold" style={{ color: C.ink }}>{c.user_name}</div>
                <div className="text-xs" style={{ color: C.ink }}>{c.text}</div>
                <div className="text-[9px] mt-0.5" style={{ color: C.inkDim }}>
                  {new Date(c.created_at).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              {canDeleteComment(c) && (
                <button
                  onClick={() => deleteComment(c.comment_id)}
                  className="opacity-50 hover:opacity-100 transition"
                  title="Apagar"
                >
                  <Trash2 className="w-3 h-3" style={{ color: C.red }} />
                </button>
              )}
            </div>
          ))}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !sending) addComment(); }}
              placeholder="Escreve um comentário…"
              maxLength={500}
              className="flex-1 px-2.5 py-1.5 rounded text-xs"
              style={{ background: 'rgba(0,0,0,0.5)', color: C.ink, border: `1px solid ${C.sandLine}`, outline: 'none' }}
              data-testid={`post-comment-input-${post.post_id}`}
            />
            <button
              onClick={addComment}
              disabled={sending || !commentText.trim()}
              className="px-2.5 py-1.5 rounded text-xs font-bold disabled:opacity-50"
              style={{ backgroundColor: C.brand, color: '#fff' }}
              data-testid={`post-comment-send-${post.post_id}`}
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * MemberRow — linha de membro com edição inline do nome, mudança de role
 * e remoção da empresa.
 * PATCH /api/companies/{id}/members/{user_id}
 * DELETE /api/companies/{id}/members/{user_id}
 */
const MemberRow = ({ member, companyId, canEdit, canManageRoles, currentUserId, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(member.name || '');
  const [saving, setSaving] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const m = member;
  const isSelf = m.user_id === currentUserId;

  useEffect(() => { setName(member.name || ''); }, [member.name]);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === (m.name || '')) {
      setEditing(false); setName(m.name || ''); return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/companies/${companyId}/members/${m.user_id}`,
        { name: trimmed },
        { withCredentials: true },
      );
      toast.success(`Nome actualizado: ${trimmed}`);
      setEditing(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao actualizar');
      setName(m.name || '');
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (newRole) => {
    if (newRole === m.role) return;
    setChangingRole(true);
    try {
      await axios.patch(
        `${API}/companies/${companyId}/members/${m.user_id}`,
        { role: newRole },
        { withCredentials: true },
      );
      toast.success(newRole === 'company_staff' ? 'Promovido a staff' : 'Despromovido a membro');
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao mudar role');
    } finally {
      setChangingRole(false);
    }
  };

  const removeMember = async () => {
    if (!window.confirm(`Remover ${m.name || m.email} da empresa?\nO membro deixa de ver rotas privadas e chat.`)) return;
    setRemoving(true);
    try {
      await axios.delete(
        `${API}/companies/${companyId}/members/${m.user_id}`,
        { withCredentials: true },
      );
      toast.success('Membro removido da empresa');
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover');
    } finally {
      setRemoving(false);
    }
  };

  // Fev/2026 — Reset password de membro (só marshall/admin)
  // Backend gera pass temporária + `force_password_change=True`.
  // A pass em plaintext é mostrada UMA vez ao marshall — não é enviada por
  // email (para evitar dependência SMTP). Marshall passa por WhatsApp/pessoalmente.
  const resetPassword = async () => {
    if (!window.confirm(`Reset da password de ${m.name || m.email}?\n\nO membro será forçado a mudar a password no próximo login.`)) return;
    setResetting(true);
    try {
      const { data } = await axios.post(
        `${API}/companies/${companyId}/members/${m.user_id}/reset-password`,
        {},
        { withCredentials: true },
      );
      // Copia automaticamente para clipboard e mostra num toast persistente.
      try { await navigator.clipboard.writeText(data.temp_password); } catch { /* clipboard bloqueado */ }
      toast.success(
        `Password temporária de ${m.name || m.email}: ${data.temp_password} (copiada para o clipboard)`,
        { duration: 15000 },
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro no reset');
    } finally {
      setResetting(false);
    }
  };

  const cancel = () => { setEditing(false); setName(m.name || ''); };
  const onKey = (e) => {
    if (e.key === 'Enter') save();
    else if (e.key === 'Escape') cancel();
  };

  return (
    <div
      className="px-4 py-2.5 flex items-center gap-3 flex-wrap"
      data-testid={`company-member-row-${m.user_id}`}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold overflow-hidden"
        style={{ backgroundColor: C.brandDim, color: C.brand }}
      >
        {m.picture ? (
          <img src={m.picture} alt={m.name} className="w-full h-full object-cover" />
        ) : (
          (m.name || m.email || '?').slice(0, 2).toUpperCase()
        )}
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={onKey}
            autoFocus maxLength={80}
            className="w-full text-sm font-semibold px-2 py-1 rounded"
            style={{
              background: 'rgba(0,0,0,0.5)', color: C.ink,
              border: `1px solid ${C.brand}`, outline: 'none',
            }}
            data-testid={`company-member-name-input-${m.user_id}`}
          />
        ) : (
          <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>
            {m.name || m.email}{isSelf && <span className="ml-2 text-[10px]" style={{ color: C.inkDim }}>(tu)</span>}
          </div>
        )}
        <div className="text-xs" style={{ color: C.inkDim }}>{m.email}</div>
      </div>
      {canEdit && !editing && (
        <button
          onClick={() => setEditing(true)}
          className="p-1.5 rounded opacity-60 hover:opacity-100 transition"
          title="Renomear membro"
          data-testid={`company-member-edit-${m.user_id}`}
        >
          <Pencil className="w-3.5 h-3.5" style={{ color: C.sand }} />
        </button>
      )}
      {editing && (
        <>
          <button
            onClick={save}
            disabled={saving}
            className="p-1.5 rounded transition"
            title="Guardar (Enter)"
            data-testid={`company-member-save-${m.user_id}`}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: C.brand }} /> :
              <Check className="w-3.5 h-3.5" style={{ color: C.green }} />}
          </button>
          <button
            onClick={cancel}
            disabled={saving}
            className="p-1.5 rounded transition"
            title="Cancelar (Esc)"
            data-testid={`company-member-cancel-${m.user_id}`}
          >
            <X className="w-3.5 h-3.5" style={{ color: C.red }} />
          </button>
        </>
      )}
      <span
        className="text-[10px] px-2 py-0.5 rounded uppercase font-bold"
        style={{
          backgroundColor: m.role === 'company_admin' ? C.brandDim : m.role === 'company_staff' ? 'rgba(196,162,117,0.20)' : 'rgba(0,0,0,0.35)',
          color: m.role === 'company_admin' ? C.brand : m.role === 'company_staff' ? C.sand : C.inkDim,
          border: `1px solid ${m.role === 'company_admin' ? C.brand : m.role === 'company_staff' ? C.sand : C.sandLine}`,
        }}
      >
        {m.role === 'company_admin' ? 'Marshall' : m.role === 'company_staff' ? 'Staff' : 'Membro'}
      </span>
      {/* Toggle role: só company_admin/admin, não pode mexer em self ou marshall */}
      {canManageRoles && !isSelf && m.role !== 'company_admin' && (
        <button
          onClick={() => changeRole(m.role === 'company_staff' ? 'user' : 'company_staff')}
          disabled={changingRole}
          className="px-2 py-1 rounded text-[10px] font-bold uppercase transition disabled:opacity-60"
          style={{
            backgroundColor: m.role === 'company_staff' ? 'rgba(0,0,0,0.35)' : C.sand,
            color: m.role === 'company_staff' ? C.sand : C.bg3,
            border: `1px solid ${C.sand}`,
          }}
          title={m.role === 'company_staff' ? 'Retirar staff — passar a membro' : 'Promover a staff da empresa'}
          data-testid={`company-member-role-toggle-${m.user_id}`}
        >
          {changingRole ? <Loader2 className="w-3 h-3 animate-spin" /> : m.role === 'company_staff' ? '↓ Retirar Staff' : '↑ Tornar Staff'}
        </button>
      )}
      {/* Remover da empresa: só company_admin/admin, não em self */}
      {canManageRoles && !isSelf && (
        <button
          onClick={removeMember}
          disabled={removing}
          className="p-1.5 rounded transition disabled:opacity-60"
          style={{ border: `1px solid ${C.red}`, backgroundColor: 'rgba(220,38,38,0.10)' }}
          title="Remover da empresa"
          data-testid={`company-member-remove-${m.user_id}`}
        >
          {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: C.red }} /> :
            <Trash2 className="w-3.5 h-3.5" style={{ color: C.red }} />}
        </button>
      )}
      {/* Reset password: só canManageRoles, não em self, não em outros marshalls */}
      {canManageRoles && !isSelf && m.role !== 'company_admin' && (
        <button
          onClick={resetPassword}
          disabled={resetting}
          className="p-1.5 rounded transition disabled:opacity-60"
          style={{ border: `1px solid ${C.sand}`, backgroundColor: 'rgba(196,162,117,0.10)' }}
          title="Gerar nova password (o membro é forçado a mudar no próximo login)"
          data-testid={`company-member-reset-${m.user_id}`}
        >
          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: C.sand }} /> :
            <Key className="w-3.5 h-3.5" style={{ color: C.sand }} />}
        </button>
      )}
    </div>
  );
};

/**
 * Company Dashboard main component.
 */
const CompanyDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, loading: authLoading } = useAuth();
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [sosData, setSosData] = useState({ sos_active: [], sos_resolved_today: [], totals: {} });
  const [hazardsData, setHazardsData] = useState({ hazards_oficiais: [], user_reports: [], sos_active: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [viewerRouteId, setViewerRouteId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pushingRoute, setPushingRoute] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [fleetMapOpen, setFleetMapOpen] = useState(false);
  // Rotas — upload/edit modals (Fev/2026)
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    name: '', description: '', file: null, coverImage: null, difficulty: '',
    route_type: '', region: '', tags: '', group_id: '', group_ids: [],
    is_private: true, vehicle_badges: [], logos: [], photos: [],
  });
  const [uploading, setUploading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [reloadFile, setReloadFile] = useState(null);
  const [reloadSaving, setReloadSaving] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  // Fev/2026 — Estatísticas + Eventos + Reset password
  const [stats, setStats] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState('30d');
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState({ open: false, title: '', description: '', route_id: '', starts_at: '', meeting: null });
  const [savingEvent, setSavingEvent] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // Só company_admin/company_staff (com company_id) OU admin global
    const isCompanyRole = ['company_admin', 'company_staff'].includes(user.role) && user.company_id;
    const isAdmin = user.role === 'admin';
    if (!isCompanyRole && !isAdmin) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  // Admin global pode gerir qualquer empresa via `?company_id=X`.
  // Company_admin/company_staff usam sempre a sua própria empresa.
  const isAdmin = user?.role === 'admin';
  const overrideCid = isAdmin ? searchParams.get('company_id') : null;
  const companyId = overrideCid || user?.company_id;

  const fetchAll = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const isManager = ['admin', 'company_admin', 'company_staff'].includes(user?.role);
      const promises = [
        axios.get(`${API}/companies/${companyId}`, { withCredentials: true }),
        axios.get(`${API}/companies/${companyId}/members`, { withCredentials: true }),
        axios.get(`${API}/routes`, { withCredentials: true }),
        axios.get(`${API}/companies/${companyId}/sos-live`, { withCredentials: true }),
        axios.get(`${API}/companies/${companyId}/hazards/live`, { withCredentials: true }),
        axios.get(`${API}/companies/${companyId}/events?scope=upcoming`, { withCredentials: true }),
      ];
      // Stats só para marshall/staff/admin (users normais não acedem)
      if (isManager) {
        promises.push(axios.get(`${API}/companies/${companyId}/stats?period=${statsPeriod}`, { withCredentials: true }));
      }
      const responses = await Promise.all(promises);
      const [companyRes, membersRes, routesRes, sosRes, hazardsRes, eventsRes, statsRes] = responses;
      setCompany(companyRes.data);
      setMembers(membersRes.data || []);
      const allRoutes = routesRes.data || [];
      setRoutes(allRoutes.filter((r) => r.company_id === companyId));
      setSosData(sosRes.data || { sos_active: [], sos_resolved_today: [], totals: {} });
      setHazardsData(hazardsRes.data || { hazards_oficiais: [], user_reports: [], sos_active: [], totals: {} });
      setEvents(eventsRes.data || []);
      if (statsRes) setStats(statsRes.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro a carregar dashboard';
      toast.error(detail);
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  // Handlers eventos + stats (Fev/2026)
  const openEventCreate = () => {
    setEventForm({ open: true, title: '', description: '', route_id: '', starts_at: '', meeting: null });
  };
  const submitEvent = async () => {
    if (!eventForm.title?.trim() || !eventForm.starts_at) {
      toast.error('Título e data/hora são obrigatórios');
      return;
    }
    setSavingEvent(true);
    try {
      const payload = {
        title: eventForm.title.trim(),
        description: eventForm.description?.trim() || null,
        route_id: eventForm.route_id || null,
        starts_at: new Date(eventForm.starts_at).toISOString(),
        meeting_lat: eventForm.meeting?.lat ?? null,
        meeting_lng: eventForm.meeting?.lng ?? null,
        meeting_name: eventForm.meeting?.address ?? null,
      };
      await axios.post(`${API}/companies/${companyId}/events`, payload, { withCredentials: true });
      toast.success('Evento criado ✅');
      setEventForm({ open: false, title: '', description: '', route_id: '', starts_at: '', meeting: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar evento');
    } finally {
      setSavingEvent(false);
    }
  };
  const cancelEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`Cancelar evento "${eventTitle}"?`)) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/events/${eventId}`, { withCredentials: true });
      toast.success('Evento cancelado');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  useEffect(() => {
    if (user && companyId) fetchAll();
  }, [companyId, user?.user_id]);

  // Auto-refresh SOS+hazards a cada 30s (mesmo pattern do MunicipioDashboard)
  useEffect(() => {
    if (!companyId) return undefined;
    const t = setInterval(async () => {
      try {
        const [sosRes, hazardsRes] = await Promise.all([
          axios.get(`${API}/companies/${companyId}/sos-live`, { withCredentials: true }),
          axios.get(`${API}/companies/${companyId}/hazards/live`, { withCredentials: true }),
        ]);
        setSosData(sosRes.data || { sos_active: [], sos_resolved_today: [], totals: {} });
        setHazardsData(hazardsRes.data || { hazards_oficiais: [], user_reports: [], sos_active: [], totals: {} });
      } catch { /* ignore poll errors */ }
    }, 30000);
    return () => clearInterval(t);
  }, [companyId]);

  const doLogout = async () => {
    await logout();
    navigate('/login');
  };

  const doPushRoute = async (routeId, routeName) => {
    setPushingRoute(routeId);
    try {
      await axios.post(`${API}/routes/${routeId}/push`, {}, { withCredentials: true });
      toast.success(`"${routeName}" enviada para os membros da empresa`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar rota');
    } finally {
      setPushingRoute(null);
    }
  };

  // === Rotas: upload/edit/delete (Fev/2026) ===
  // Reutiliza `UploadRouteDialog` e `EditRouteDialog` (mesmos componentes do
  // Dashboard admin global) — mas força `push_to_app=true|false` via toggle
  // e `is_private` = true no upload (backend também força automaticamente).
  const canManageRoutes = ['admin', 'company_admin', 'company_staff'].includes(user?.role);

  const openUpload = () => {
    setUploadData({
      name: '', description: '', file: null, coverImage: null, difficulty: '',
      route_type: '', region: '', tags: '', group_id: '', group_ids: [],
      is_private: true, vehicle_badges: [], logos: [], photos: [],
    });
    setUploadOpen(true);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.name) {
      toast.error('Ficheiro e nome são obrigatórios');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      if (uploadData.coverImage) formData.append('cover_image', uploadData.coverImage);
      const params = new URLSearchParams({
        name: uploadData.name,
        push_to_app: uploadData.draft ? 'false' : 'true',
        // Fev/2026 (Ricardo) — admin global via `?company_id=X` precisa de
        // reenviar company_id como query param ao backend (que não lê a URL
        // do browser). company_admin/staff ignoram este param (auto-scope).
        ...(companyId && { company_id: companyId }),
        ...(uploadData.description && { description: uploadData.description }),
        ...(uploadData.difficulty && { difficulty: uploadData.difficulty }),
        ...(uploadData.route_type && { route_type: uploadData.route_type }),
        ...(uploadData.region && { region: uploadData.region }),
        ...(uploadData.tags && { tags: uploadData.tags }),
        ...(uploadData.vehicle_badges?.length > 0 && { vehicle_badges: uploadData.vehicle_badges.join(',') }),
        ...(uploadData.draft && { notify_on_publish: 'false' }),
      });
      // ⚠️ Sem Content-Type manual — axios 1.x não gera boundary (bug Fev/2026)
      const response = await axios.post(
        `${API}/routes/upload?${params.toString()}`,
        formData,
        { withCredentials: true },
      );
      const created = response.data;
      // Uploads adicionais (logos / photos) — best-effort
      if (created?.route_id && uploadData.logos?.length > 0) {
        try {
          const fd = new FormData();
          uploadData.logos.forEach((f) => fd.append('files', f));
          await axios.post(`${API}/routes/${created.route_id}/logos`, fd, {
            withCredentials: true,
          });
        } catch (err) { toast.warning('Logos não anexados'); }
      }
      if (created?.route_id && uploadData.photos?.length > 0) {
        try {
          const fd = new FormData();
          uploadData.photos.forEach((f) => fd.append('files', f));
          await axios.post(`${API}/routes/${created.route_id}/photos`, fd, {
            withCredentials: true,
          });
        } catch (err) { toast.warning('Fotos não anexadas'); }
      }
      toast.success(uploadData.draft ? 'Guardada como rascunho' : 'Rota carregada e enviada aos membros 🔔');
      setUploadOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar rota');
    } finally {
      setUploading(false);
    }
  };

  const openEdit = (route) => {
    setEditData({
      route_id: route.route_id,
      name: route.name || '',
      description: route.description || '',
      difficulty: route.difficulty || '',
      route_type: route.route_type || '',
      // rotas de empresa: forçadas privadas, sem grupos externos
      group_ids: [],
      is_private: true,
      vehicle_badges: route.vehicle_badges || [],
      logo_count: route.logo_count || 0,
      photo_count: route.photo_count || 0,
      has_cover: !!(route.cover_image_path || route.has_cover),
      _coverV: Date.now(),
      newLogos: [],
      newPhotos: [],
    });
    setEditOpen(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editData.name?.trim()) { toast.error('Nome obrigatório'); return; }
    setEditSaving(true);
    try {
      const payload = {
        name: editData.name.trim(),
        description: editData.description,
        difficulty: editData.difficulty || null,
        route_type: editData.route_type || null,
        vehicle_badges: Array.isArray(editData.vehicle_badges) ? editData.vehicle_badges : [],
      };
      await axios.patch(`${API}/routes/${editData.route_id}`, payload, { withCredentials: true });
      if (editData.newLogos?.length > 0) {
        const fd = new FormData();
        editData.newLogos.forEach((f) => fd.append('files', f));
        try {
          await axios.post(`${API}/routes/${editData.route_id}/logos`, fd, {
            withCredentials: true,
          });
        } catch { toast.warning('Falha logos'); }
      }
      if (editData.newPhotos?.length > 0) {
        const fd = new FormData();
        editData.newPhotos.forEach((f) => fd.append('files', f));
        try {
          await axios.post(`${API}/routes/${editData.route_id}/photos`, fd, {
            withCredentials: true,
          });
        } catch { toast.warning('Falha fotos'); }
      }
      toast.success('Rota actualizada');
      setEditOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setEditSaving(false);
    }
  };

  const handleReloadContent = async () => {
    if (!reloadFile) { toast.error('Escolhe um GPX/KML'); return; }
    if (!window.confirm('Substituir o ficheiro desta rota?\n\nO route_id mantém-se e ratings/comentários são preservados.')) return;
    setReloadSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', reloadFile);
      await axios.put(`${API}/routes/${editData.route_id}/content`, fd, {
        withCredentials: true,
      });
      toast.success('Ficheiro substituído — estatísticas recalculadas');
      setReloadFile(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao substituir');
    } finally {
      setReloadSaving(false);
    }
  };

  const patchRouteLocal = (routeId, patch) => {
    setRoutes((prev) => prev.map((rt) => rt.route_id === routeId ? { ...rt, ...patch } : rt));
  };

  const handleDeleteRoute = async (routeId, routeName) => {
    if (!window.confirm(`Apagar a rota "${routeName}"?\n\nA rota deixa de estar disponível para os membros da empresa.`)) return;
    try {
      await axios.delete(`${API}/routes/${routeId}`, { withCredentials: true });
      toast.success('Rota apagada');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao apagar');
    }
  };

  if (authLoading || (loading && !company && companyId)) {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center"
        style={{ backgroundColor: C.bg, color: C.ink }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: C.brand }} />
      </div>
    );
  }

  // Admin global sem `?company_id=X` → mostrar mensagem em vez de spinner infinito
  if (!companyId) {
    const adminMsg = isAdmin
      ? 'Estás como admin global. Escolhe uma empresa a gerir no botão "Empresas" no topo do Dashboard.'
      : `A sua conta (${user?.email || ''}) tem role ${user?.role || ''} mas não está associada a nenhuma empresa. Peça a um administrador para o adicionar via convite.`;
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center p-6"
        style={{ backgroundColor: C.bg, color: C.ink }}
        data-testid="company-dashboard-empty"
      >
        <div className="max-w-md text-center space-y-4">
          <Building2 className="w-16 h-16 mx-auto" style={{ color: C.brand }} />
          <h1 className="text-2xl font-bold">
            {isAdmin ? 'Nenhuma empresa seleccionada' : 'Sem empresa associada'}
          </h1>
          <p className="text-sm" style={{ color: C.inkDim }}>{adminMsg}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 rounded-md font-semibold"
            style={{ backgroundColor: C.brand, color: '#fff' }}
            data-testid="empty-back-btn"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  const totals = {
    routes: routes.length,
    members: members.length,
    sos_active: (sosData.totals?.active) || 0,
    hazards: (hazardsData.totals?.hazards || 0) + (hazardsData.totals?.user_reports || 0),
  };

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor: C.bg }} data-testid="company-dashboard">
      {/* Header */}
      <header
        className="sticky top-0 z-30 backdrop-blur"
        style={{
          backgroundColor: 'rgba(26,22,19,0.9)',
          borderBottom: `1px solid ${C.sandLine}`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: C.brandDim, border: `1px solid ${C.brand}` }}
            >
              {company?.logo_path ? (
                <img
                  src={`${BACKEND_URL}/${company.logo_path}?t=${encodeURIComponent(company.updated_at || '')}`}
                  alt={company.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Building2 className="w-5 h-5" style={{ color: C.brand }} />
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.inkDim }}>
                Painel Empresa
              </div>
              <div className="text-lg font-bold leading-tight" style={{ color: C.ink }}>
                {company?.name || '—'}
                {company?.nif && (
                  <span className="ml-2 text-sm font-normal" style={{ color: C.inkDim }}>
                    · NIF {company.nif}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Fix B (Fev/2026): admin global consegue voltar ao Dashboard admin */}
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
                style={{ backgroundColor: 'rgba(0,0,0,0.35)', color: C.sand, border: `1px solid ${C.sandLine}` }}
                title="Voltar ao dashboard admin global"
                data-testid="header-back-admin-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Dashboard admin
              </button>
            )}
            {/* Fix C (Fev/2026): abre painel de rotas full-screen scoped à empresa */}
            <button
              onClick={() => navigate(`/empresa/rotas?company_id=${encodeURIComponent(companyId)}`)}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
              style={{ backgroundColor: 'rgba(232,122,42,0.14)', color: C.brand, border: `1px solid ${C.brand}` }}
              title="Painel completo de rotas da empresa"
              data-testid="header-routes-panel-btn"
            >
              <List className="w-3.5 h-3.5" /> Rotas
            </button>
            <button
              onClick={() => setFleetMapOpen(true)}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
              style={{ backgroundColor: C.brandDim, color: C.brand, border: `1px solid ${C.brand}` }}
              title="Mapa completo com a localização de todos os membros"
              data-testid="header-fleet-map-btn"
            >
              <Navigation className="w-3.5 h-3.5" /> Mapa frota
            </button>
            <button
              onClick={fetchAll}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
              style={{ color: C.ink, border: `1px solid ${C.sandLine}` }}
              data-testid="company-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Atualizar
            </button>
            {/* Voltar ao mapa → volta ao principal com mapa (a sessão continua). */}
            <button
              onClick={() => navigate('/dashboard')}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
              style={{ backgroundColor: C.brand, color: '#fff' }}
              data-testid="company-exit-btn"
              title="Voltar à página principal com mapa"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao mapa
            </button>
          </div>
        </div>
      </header>

      <FleetWidget companyId={companyId} onOpenMap={() => setFleetMapOpen(true)} />

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard icon={RouteIcon} label="Rotas da empresa" value={totals.routes} sub="Privadas · scoped" />
          <KpiCard icon={Users} label="Membros" value={totals.members} sub={`${company?.subscription_active ? 'subscrição activa' : 'sem subscrição'}`} tint={C.sand} />
          <KpiCard icon={ShieldAlert} label="SOS activos" value={totals.sos_active} sub="agora" tint={totals.sos_active > 0 ? C.red : C.green} />
          <KpiCard icon={AlertTriangle} label="Avisos activos" value={totals.hazards} sub="IPMA + reports" tint={C.brand} />
        </div>

        {/* SOS + Avisos */}
        <SosPanel sosData={sosData} onOpen={(d) => setDetail({ type: 'sos', data: d })} />
        <AlertsPanel
          hazardsData={hazardsData}
          onOpenHazard={(d) => setDetail({ type: 'hazard', data: d })}
          onOpenReport={(d) => setDetail({ type: 'user_report', data: d })}
        />

        {/* Mural interno da empresa */}
        <MuralPanel companyId={companyId} me={user} />

        {/* Fev/2026 — KPIs de utilização (só marshall/staff/admin) */}
        {stats && ['admin', 'company_admin', 'company_staff'].includes(user?.role) && (
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
            data-testid="company-stats-panel"
          >
            <div className="px-4 py-3 flex items-center gap-2 border-b flex-wrap" style={{ borderColor: C.sandLine }}>
              <TrendingUp className="w-4 h-4" style={{ color: C.brand }} />
              <div className="text-sm font-bold uppercase tracking-wider flex-1" style={{ color: C.ink }}>
                Estatísticas de utilização
              </div>
              <div className="flex gap-1">
                {['7d', '30d', '90d', 'all'].map((p) => (
                  <button
                    key={p}
                    onClick={() => { setStatsPeriod(p); }}
                    className="px-2 py-1 rounded text-[10px] font-bold uppercase transition"
                    style={{
                      backgroundColor: statsPeriod === p ? C.brand : 'rgba(0,0,0,0.35)',
                      color: statsPeriod === p ? '#fff' : C.inkDim,
                      border: `1px solid ${statsPeriod === p ? C.brand : C.sandLine}`,
                    }}
                    data-testid={`stats-period-${p}`}
                  >
                    {p === 'all' ? 'Sempre' : p}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4">
              {[
                { label: 'Sessões iniciadas', value: stats.starts, sub: `${stats.unique_users_started} utilizadores` },
                { label: 'Concluídas', value: stats.completes, sub: `${stats.completion_rate}% taxa` },
                { label: 'Quilómetros totais', value: `${stats.total_km} km`, sub: `${stats.unique_users_completed} finalizaram` },
                { label: 'Membros', value: stats.total_members, sub: `${stats.total_routes} rotas activas` },
              ].map((kpi, i) => (
                <div
                  key={i}
                  className="rounded-lg p-3"
                  style={{ backgroundColor: 'rgba(0,0,0,0.35)', border: `1px solid ${C.sandLine}` }}
                >
                  <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: C.inkDim }}>{kpi.label}</div>
                  <div className="text-2xl font-black mt-1" style={{ color: C.brand }}>{kpi.value}</div>
                  <div className="text-[10px] mt-0.5" style={{ color: C.inkDim }}>{kpi.sub}</div>
                </div>
              ))}
            </div>
            {stats.top_routes?.length > 0 && (
              <div className="px-4 pb-4">
                <div className="text-[10px] uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1.5" style={{ color: C.inkDim }}>
                  <Trophy className="w-3 h-3" /> Top rotas
                </div>
                <div className="space-y-1">
                  {stats.top_routes.map((tr, i) => (
                    <div key={tr.route_id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.20)' }}>
                      <div className="truncate flex-1" style={{ color: C.ink }}>#{i + 1} · {tr.name}</div>
                      <div className="flex gap-3 text-[10px] font-bold" style={{ color: C.inkDim }}>
                        <span>{tr.starts} sessões</span>
                        <span style={{ color: C.brand }}>{tr.km.toFixed(1)} km</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fev/2026 — Eventos da empresa (só marshall/staff/admin cria; membros vêem) */}
        <div
          className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
          data-testid="company-events-panel"
        >
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: C.sandLine }}>
            <Calendar className="w-4 h-4" style={{ color: C.brand }} />
            <div className="text-sm font-bold uppercase tracking-wider flex-1" style={{ color: C.ink }}>
              Próximos eventos ({events.length})
            </div>
            {['admin', 'company_admin', 'company_staff'].includes(user?.role) && (
              <button
                onClick={openEventCreate}
                className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition"
                style={{ backgroundColor: C.brand, color: '#fff' }}
                data-testid="company-create-event-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Criar evento
              </button>
            )}
          </div>
          {events.length === 0 ? (
            <div className="p-6 text-center text-xs" style={{ color: C.inkDim }}>
              Sem eventos agendados. Cria um com ponto de encontro no mapa para reunir os membros.
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: C.sandLine }}>
              {events.map((evt) => (
                <div
                  key={evt.event_id}
                  className="px-4 py-2.5 flex items-center justify-between gap-3"
                  data-testid={`event-row-${evt.event_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>
                      {evt.title}
                    </div>
                    <div className="text-xs mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: C.inkDim }}>
                      <span>📅 {new Date(evt.starts_at).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })}</span>
                      {evt.meeting_name && <span className="truncate">📍 {evt.meeting_name}</span>}
                      {evt.route_id && <span>🛣️ Rota associada</span>}
                    </div>
                  </div>
                  {['admin', 'company_admin', 'company_staff'].includes(user?.role) && (
                    <button
                      onClick={() => cancelEvent(evt.event_id, evt.title)}
                      className="p-1.5 rounded transition"
                      style={{ border: `1px solid ${C.red}`, backgroundColor: 'rgba(220,38,38,0.10)' }}
                      title="Cancelar evento"
                      data-testid={`event-cancel-${evt.event_id}`}
                    >
                      <XCircle className="w-4 h-4" style={{ color: C.red }} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Rotas da empresa */}
        <div
          className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
          data-testid="company-routes-table"
        >
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: C.sandLine }}>
            <RouteIcon className="w-4 h-4" style={{ color: C.brand }} />
            <div className="text-sm font-bold uppercase tracking-wider flex-1" style={{ color: C.ink }}>
              Rotas ({routes.length})
            </div>
            {canManageRoutes && ['admin', 'staff'].includes(user?.role) && (
              <button
                onClick={openUpload}
                className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition"
                style={{ backgroundColor: C.brand, color: '#fff' }}
                data-testid="company-upload-route-btn"
              >
                <RouteIcon className="w-3.5 h-3.5" /> Nova rota
              </button>
            )}
          </div>
          {routes.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: C.inkDim }}>
              Ainda não há rotas privadas associadas a esta empresa.
              Pede ao administrador global para associar rotas.
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: C.sandLine }}>
              {routes.map((r) => (
                <div
                  key={r.route_id}
                  className="px-4 py-3 flex items-center justify-between gap-4 hover:bg-black/10"
                  data-testid={`company-route-row-${r.route_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>
                      {r.name}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: C.inkDim }}>
                      {r.difficulty ? `${r.difficulty} · ` : ''}
                      {r.distance_km ? `${Number(r.distance_km).toFixed(1)} km` : '—'}
                      {r.region ? ` · ${r.region}` : ''}
                      {r.pushed_to_app && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded uppercase font-bold" style={{ backgroundColor: C.greenDim, color: C.green }}>
                          Enviada
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canManageRoutes && (
                      <>
                        <button
                          onClick={() => openEdit(r)}
                          className="px-2.5 py-1.5 rounded-md text-xs font-bold flex items-center gap-1 transition"
                          style={{ backgroundColor: 'rgba(0,0,0,0.35)', color: C.sand, border: `1px solid ${C.sandLine}` }}
                          title="Editar rota"
                          data-testid={`company-edit-route-${r.route_id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteRoute(r.route_id, r.name)}
                          className="px-2.5 py-1.5 rounded-md text-xs font-bold flex items-center gap-1 transition"
                          style={{ backgroundColor: 'rgba(220,38,38,0.10)', color: C.red, border: `1px solid ${C.red}` }}
                          title="Apagar rota"
                          data-testid={`company-delete-route-${r.route_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => doPushRoute(r.route_id, r.name)}
                      disabled={pushingRoute === r.route_id}
                      className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-60"
                      style={{ backgroundColor: C.sand, color: C.bg3 }}
                      title="Enviar para os membros da empresa"
                      data-testid={`company-push-route-${r.route_id}`}
                    >
                      {pushingRoute === r.route_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Send className="w-3.5 h-3.5" />
                      )}
                      Enviar
                    </button>
                    <button
                      onClick={() => setViewerRouteId(r.route_id)}
                      className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition"
                      style={{ backgroundColor: C.brand, color: '#fff' }}
                      title="Visualizar rota no mapa"
                      data-testid={`company-view-route-${r.route_id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Ver rota
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Membros */}
        <div
          className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
          data-testid="company-members-table"
        >
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: C.sandLine }}>
            <Users className="w-4 h-4" style={{ color: C.brand }} />
            <div className="text-sm font-bold uppercase tracking-wider flex-1" style={{ color: C.ink }}>
              Membros ({members.length})
            </div>
            <button
              onClick={() => setInviteOpen(true)}
              className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition"
              style={{ backgroundColor: C.brand, color: '#fff' }}
              data-testid="company-invite-btn"
            >
              <UserPlus className="w-3.5 h-3.5" /> Convidar
            </button>
          </div>
          {members.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: C.inkDim }}>
              Sem membros. Convida através de `/api/companies/{companyId}/invite`.
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: C.sandLine }}>
              {members.map((m) => (
                <MemberRow
                  key={m.user_id}
                  member={m}
                  companyId={companyId}
                  canEdit={['admin', 'company_admin', 'company_staff'].includes(user?.role)}
                  canManageRoles={['admin', 'company_admin'].includes(user?.role)}
                  currentUserId={user?.user_id}
                  onUpdated={fetchAll}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal de visualização da rota */}
      {viewerRouteId && (
        <RouteViewerModal
          routeId={viewerRouteId}
          open={!!viewerRouteId}
          onClose={() => setViewerRouteId(null)}
          user={user}
          liveOverlay={true}
          liveOverlayMode="company"
          companyId={companyId}
        />
      )}

      {/* Modal de detalhe SOS/Avisos */}
      <AlertDetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        type={detail?.type}
        data={detail?.data}
        municipalityName={company?.name}
        onChanged={fetchAll}
      />

      {/* Modal de convite de membro */}
      <InviteMemberModal
        open={inviteOpen}
        onClose={() => { setInviteOpen(false); fetchAll(); }}
        companyId={companyId}
        canInviteAdmin={user?.role === 'admin'}
      />

      {/* Modal do mapa completo da frota */}
      <FleetMapModal
        open={fleetMapOpen}
        onClose={() => setFleetMapOpen(false)}
        companyId={companyId}
      />

      {/* Upload de rota (Fev/2026 — B2B) — reutiliza componente do Dashboard admin.
           Backend força `is_private=true` e injecta `company_id` a partir do user
           quando role é company_admin/staff. `availableGroups=[]` porque rotas
           de empresa não usam grupos (a scope é feita por company_id). */}
      {canManageRoutes && (
        <UploadRouteDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          data={uploadData}
          setData={setUploadData}
          uploading={uploading}
          onSubmit={handleUpload}
          availableGroups={availableGroups}
        />
      )}

      {/* Edit rota — mesmo componente do admin, scoped à empresa */}
      {canManageRoutes && (
        <EditRouteDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          data={editData}
          setData={setEditData}
          editSaving={editSaving}
          onSubmit={handleEditSave}
          availableGroups={availableGroups}
          reloadFile={reloadFile}
          setReloadFile={setReloadFile}
          reloadSaving={reloadSaving}
          onReloadContent={handleReloadContent}
          patchRoute={patchRouteLocal}
          api={API}
        />
      )}

      {/* Modal criar evento — MeetingPointPicker inline */}
      {eventForm.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
          onClick={() => setEventForm({ ...eventForm, open: false })}
          data-testid="event-create-modal"
        >
          <div
            className="w-full max-w-2xl rounded-xl border max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: C.bg2, borderColor: C.brand }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.sandLine}` }}>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" style={{ color: C.brand }} />
                <h3 className="text-lg font-bold" style={{ color: C.ink }}>Novo evento</h3>
              </div>
              <button onClick={() => setEventForm({ ...eventForm, open: false })} data-testid="event-modal-close">
                <X className="w-5 h-5" style={{ color: C.inkDim }} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider" style={{ color: C.inkDim }}>Título</label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  placeholder="Passeio Serra do Marão · Sábado"
                  className="w-full mt-1 px-3 py-2 rounded-md text-sm"
                  style={{ background: 'rgba(0,0,0,0.35)', color: C.ink, border: `1px solid ${C.sandLine}`, outline: 'none' }}
                  data-testid="event-title-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider" style={{ color: C.inkDim }}>Data e hora</label>
                <input
                  type="datetime-local"
                  value={eventForm.starts_at}
                  onChange={(e) => setEventForm({ ...eventForm, starts_at: e.target.value })}
                  className="w-full mt-1 px-3 py-2 rounded-md text-sm"
                  style={{ background: 'rgba(0,0,0,0.35)', color: C.ink, border: `1px solid ${C.sandLine}`, outline: 'none', colorScheme: 'dark' }}
                  data-testid="event-datetime-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider" style={{ color: C.inkDim }}>Rota (opcional)</label>
                <select
                  value={eventForm.route_id}
                  onChange={(e) => setEventForm({ ...eventForm, route_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 rounded-md text-sm"
                  style={{ background: 'rgba(0,0,0,0.35)', color: C.ink, border: `1px solid ${C.sandLine}`, outline: 'none' }}
                  data-testid="event-route-select"
                >
                  <option value="">— sem rota associada —</option>
                  {routes.map((r) => (
                    <option key={r.route_id} value={r.route_id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider" style={{ color: C.inkDim }}>Descrição (opcional)</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder="Instruções, equipamento necessário, contactos…"
                  rows={2}
                  className="w-full mt-1 px-3 py-2 rounded-md text-sm resize-none"
                  style={{ background: 'rgba(0,0,0,0.35)', color: C.ink, border: `1px solid ${C.sandLine}`, outline: 'none' }}
                  data-testid="event-description-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: C.inkDim }}>
                  <MapPin className="w-3 h-3" /> Ponto de encontro
                </label>
                <div className="mt-1">
                  <MeetingPointPicker
                    initialLat={39.5}
                    initialLon={-8.0}
                    height={260}
                    onPick={(m) => setEventForm((prev) => ({ ...prev, meeting: m }))}
                    testIDPrefix="event-meeting"
                  />
                </div>
              </div>
              <button
                onClick={submitEvent}
                disabled={savingEvent || !eventForm.title?.trim() || !eventForm.starts_at}
                className="w-full py-2.5 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition disabled:opacity-60"
                style={{ backgroundColor: C.brand, color: '#fff' }}
                data-testid="event-submit-btn"
              >
                {savingEvent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {savingEvent ? 'A criar…' : 'Criar evento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDashboard;
